// packages/frontend/src/features/recipes/CoverPlaceholder.tsx
//
// Pretty fallback shown when a recipe has no cover photo. Picks a
// food-inspired gradient deterministically from the recipe's id (same recipe
// always renders the same color — not random per render), and overlays the
// title's first letter as a large faint letterform. More informative AND
// nicer than the old "broken image" placeholder.
import type { JSX } from "react";

/** Eight food-themed dark gradients calibrated for the dark theme.
 * All colors are deep enough that a near-white letterform reads at AA. */
const PALETTE: ReadonlyArray<{ from: string; to: string }> = [
  { from: "#7f1d1d", to: "#450a0a" }, // tomato / red-900
  { from: "#9a3412", to: "#431407" }, // pumpkin / orange-900
  { from: "#854d0e", to: "#422006" }, // wheat / amber-900
  { from: "#365314", to: "#1a2e05" }, // sage / lime-900
  { from: "#14532d", to: "#052e1d" }, // herb / emerald-900
  { from: "#155e75", to: "#083344" }, // ocean / cyan-900
  { from: "#5b21b6", to: "#2e1065" }, // plum / violet-900
  { from: "#831843", to: "#500724" }, // berry / pink-900
];

/** djb2-style string hash. Deterministic, no crypto. */
function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
  }
  return Math.abs(h | 0);
}

/** First display character of the title. Falls back to "?" for empty titles. */
function firstChar(title: string): string {
  const trimmed = title.trim();
  if (trimmed.length === 0) return "?";
  // Use codepoint-aware iteration so multi-byte glyphs (emoji, CJK) don't break.
  return [...trimmed][0]!.toUpperCase();
}

interface Props {
  /** Stable seed — usually the recipe id. */
  seed: string;
  /** Recipe title; first letter is overlaid. */
  title: string;
  className?: string;
  /** Optional override for the letter size (tailwind text-* classes). */
  letterClassName?: string;
}

export function CoverPlaceholder({
  seed,
  title,
  className,
  letterClassName = "text-6xl",
}: Props): JSX.Element {
  const idx = hashString(seed) % PALETTE.length;
  const { from, to } = PALETTE[idx]!;
  const style: React.CSSProperties = {
    backgroundImage: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
  };
  return (
    <div
      className={`relative flex items-center justify-center overflow-hidden ${className ?? ""}`}
      style={style}
      aria-hidden="true"
      data-testid="cover-placeholder"
    >
      <span
        className={`font-bold text-white/15 select-none leading-none ${letterClassName}`}
      >
        {firstChar(title)}
      </span>
    </div>
  );
}
